import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { DateTime } from 'luxon';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { HttpService } from '@sondermind/http';
import {
  IAPIFetchResponse,
  IAPIServiceRequestOptions,
  IAPIListResponse,
  IAPIFetchRequestOptions
} from '@sondermind/utilities/models-http';

import { TwilioVideoToken } from '@sondermind/utilities/models-scheduling';

import { ScheduledEvent, ScheduledEventResponse } from '../models/scheduled-event';

@Injectable({
  providedIn: 'root'
})
export class ScheduledEventsHttpService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpService,
  ) {}

  fetch(id: number | string, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<IAPIFetchResponse<ScheduledEvent>> {
    const url = `${this.configurationService.env.svcBase}/scheduling/scheduled_events/${id}`;
    const params = this.http.generateFetchParams(opts);
    return this.http.get<IAPIFetchResponse<ScheduledEventResponse>>(url, params).pipe(
      map((resp) => ({ ...resp, data: new ScheduledEvent(resp.data) })),
      this.http.handleHttpError(opts.error)
    );
  }

  livekitToken(id: number | string, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<IAPIFetchResponse<TwilioVideoToken>> {
    const url = `${this.configurationService.env.svcBase}/scheduling/scheduled_events/${id}/livekit_token?consent_ready=true`;
    const params = this.http.generateFetchParams(opts);

    return this.http.get<IAPIFetchResponse<TwilioVideoToken>>(url, params).pipe(
      map((resp) => ({ ...resp, data: TwilioVideoToken.fromJson(resp.data) })),
      this.http.handleHttpError(opts.error)
    );
  }

  startLivekitEgress(id: number, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<SMEgressResponse> {
    const url = `${this.configurationService.env.svcBase}/scheduling/livekit_egress`;
    const params = this.http.generateFetchParams(opts);

    return this.http
      .post<IAPIFetchResponse<SMEgressResponse>>(url, { calendar_event_id: id }, params)
      .pipe(
        map((res) => res?.data ?? null),
        this.http.handleHttpError()
      );
  }

  stopLivekitEgress(id: number, persona: string, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<SMEgressResponse> {
    const url = `${this.configurationService.env.svcBase}/scheduling/calendar_events/${id}/revoke_transcription_consent`;
    const params = this.http.generateFetchParams(opts);
    return this.http
      .patch<IAPIFetchResponse<SMEgressResponse>>(url, { initiated_by_persona: persona }, params)
      .pipe(
        map((res) => res?.data ?? null),
        this.http.handleHttpError()
      );
  }

  startLivekitMultiEgress(id: number, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<SMMultiEgressResponse> {
    const url = `${this.configurationService.env.svcBase}/scheduling/livekit_recording/start`;
    const params = this.http.generateFetchParams(opts);

    return this.http
      .post<IAPIFetchResponse<SMMultiEgressResponse>>(url, { calendar_event_id: id }, params)
      .pipe(
        map((res) => res?.data ?? null),
        this.http.handleHttpError()
      );
  }

  stopLivekitMultiEgress(id: number, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<SMMultiEgressResponse> {
    const url = `${this.configurationService.env.svcBase}/scheduling/livekit_recording/stop`;
    const params = this.http.generateFetchParams(opts);
    return this.http
      .post<IAPIFetchResponse<SMMultiEgressResponse>>(url, { calendar_event_id: id }, params)
      .pipe(
        map((res) => res?.data ?? null),
        this.http.handleHttpError()
      );
  }

  getStatusLivekitMultiEgress(id: number, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<SMMultiEgressResponse> {
    const url = `${this.configurationService.env.svcBase}/scheduling/livekit_recording/${id}/status`;
    const params = this.http.generateFetchParams(opts);
    return this.http
      .get<IAPIFetchResponse<SMMultiEgressResponse>>(url, params)
      .pipe(
        map((res) => res?.data ?? null),
        this.http.handleHttpError()
      );
  }

  update(eventId: string | number, updateParams: ScheduledEventUpdateParams, opts: Partial<IAPIServiceRequestOptions> = {}): Observable<IAPIFetchResponse<ScheduledEvent>> {
    const params = this.http.generateServiceParams(opts);

    return this.http
      .patch<IAPIFetchResponse<ScheduledEventResponse>>(`${this.configurationService.env.svcBase}/scheduling/scheduled_events/${eventId}`, { scheduled_event: updateParams }, params)
      .pipe(
        map((resp) => ({ ...resp, data: new ScheduledEvent(resp.data) })),
        this.http.handleHttpError(opts.error)
      );
  }

  cancel$(eventId: string | number, notifyCancellation: boolean = false): Observable<ScheduledEvent> {
    return this.http.post< IAPIFetchResponse<ScheduledEventResponse> >(
      `${this.configurationService.env.svcBase}/scheduling/scheduled_events/${eventId}/cancel`,
      { scheduled_event: { notify_about_cancellation: notifyCancellation } }
    ).pipe(
      map((resp) => new ScheduledEvent(resp?.data)),
      this.http.handleHttpError({})
    );
  }

  trace(eventId: string | number, joined_room: boolean = false): Observable<ScheduledEvent> {
    return this.http.post< IAPIFetchResponse<ScheduledEventResponse> >(
      `${this.configurationService.env.svcBase}/scheduling/scheduled_events/${eventId}/trace`,
      { activity_time: DateTime.now().toISO({ includeOffset: true }), joined_room }
    ).pipe(
      map((resp) => new ScheduledEvent(resp?.data)),
      this.http.handleHttpError({})
    );
  }

  index(options: Partial<ScheduledEventsIndexOptions> = {}): Observable<ScheduledEvent[]> {
    const url = `${this.configurationService.env.svcBase}/scheduling/scheduled_events`;

    const params = new HttpParams()
      .append('view', 'with_questionnaires')
      .append('filter[for_date]', options.forDate.toISOString());

    return this.http
      .get<IAPIListResponse<ScheduledEventResponse>>(url, params)
      .pipe(
        map((res) => res?.data?.map((schedEvent) => new ScheduledEvent(schedEvent))),
        this.http.handleHttpError()
      );
  }

  getClaimStatus$(patientIds: number[]): Observable<ClaimStatusResponse[]> {
    return this.http
      .post<IAPIListResponse<ClaimStatusResponse>>(`${this.configurationService.env.svcBase}/scheduling/patients/check-claim-ready`,
      { patient_ids: patientIds })
      .pipe(
        map((res) => res?.data),
        this.http.handleHttpError()
      );
  }
}

export type ScheduledEventUpdateParams = {
  session_type: string;
  title?: string;
  start_time: Date;
  end_time: Date;
  location_id: number;
  patient_id: number;
  notify_about_cancellation: boolean;
};

export type ScheduledEventsIndexOptions = {
  userTimeZone: string;
  forDate: Date;
  view: boolean;
};

export interface SMEgressResponse {
  egress_id: string;
  room_id?: string;
  room_name: string;
  status: string;
}
export interface SMMultiEgressResponse {
  egress_id: string;
  active: boolean;
  calendar_event_id: number;
}
export interface ClaimStatusResponse {
  patient_id: number;
  blocker_priority: number;
  blocker_message: string;
}
